import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Radar, Trash2, Zap, BellRing } from "lucide-react";

export default function Wanted() {
  const [text, setText] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [demands, setDemands] = useState(null);
  const [posting, setPosting] = useState(false);
  const [instant, setInstant] = useState(null); // matches returned right after posting

  const load = () => api.get("/match/demands/mine").then(({ data }) => setDemands(data.demands)).catch(() => setDemands([]));
  useEffect(() => { load(); }, []);

  const post = async (e) => {
    e.preventDefault();
    if (text.trim().length < 3) return;
    setPosting(true);
    setInstant(null);
    try {
      const { data } = await api.post("/match/demands", {
        text: text.trim(),
        max_price: maxPrice ? Number(maxPrice) : null,
      });
      setText("");
      setMaxPrice("");
      setInstant(data.matches);
      toast.success(
        data.matches.length
          ? `${data.matches.length} live match${data.matches.length > 1 ? "es" : ""} found instantly!`
          : "We're on the hunt — you'll get an email the second it lands"
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/match/demands/${id}`);
      toast.success("Want removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="wanted-page">
      <div className="text-xs uppercase tracking-widest text-[#FF3B30] flex items-center gap-2">
        <Radar className="h-3.5 w-3.5" /> AI demand matching
      </div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Tell us what you want</h1>
      <p className="mt-3 text-neutral-400 text-sm leading-relaxed max-w-xl">
        Describe anything — our AI scans every live listing by <em>meaning</em>, not keywords.
        If it's live, you'll see it instantly. If not, the second a seller uploads it,
        you get an email. <span className="text-white font-semibold">Matched in minutes, not days.</span>
      </p>

      <form onSubmit={post} className="mt-8 rounded-xl border border-[#27272A] bg-[#121212] p-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="demand-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={200}
            placeholder='e.g. "durable phone cases under $3 per unit" or "something to keep drinks cold camping"'
            className="flex-1 bg-black/40 border-[#27272A]"
          />
          <Input
            data-testid="demand-price-input"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value.replace(/[^0-9.]/g, ""))}
            placeholder="Max $ (optional)"
            className="sm:w-36 bg-black/40 border-[#27272A]"
          />
          <Button
            type="submit"
            disabled={posting || text.trim().length < 3}
            data-testid="post-demand-btn"
            className="rounded-full bg-[#FF3B30] hover:bg-[#e0342a] text-white h-10 px-6 font-semibold"
          >
            <Zap className="h-4 w-4 mr-2" />
            {posting ? "Matching…" : "Match me"}
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
          <BellRing className="h-3.5 w-3.5" />
          Up to 10 open wants. We email you the moment a new upload matches.
        </div>
      </form>

      {instant && instant.length > 0 && (
        <section className="mt-8" data-testid="instant-matches">
          <h2 className="font-bold text-lg text-[#34C759]">⚡ Live right now — {instant.length} match{instant.length > 1 ? "es" : ""}</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {instant.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <section className="mt-10">
        <h2 className="font-bold text-lg">Your open wants</h2>
        <div className="mt-4 space-y-4">
          {demands === null && <div className="text-sm text-neutral-500">Loading…</div>}
          {demands?.length === 0 && (
            <div className="text-sm text-neutral-500 rounded-xl border border-dashed border-[#27272A] p-8 text-center">
              Nothing yet — post your first want above and let the AI hunt for you.
            </div>
          )}
          {demands?.map((d) => (
            <div key={d.id} data-testid={`demand-row-${d.id}`} className="rounded-xl border border-[#27272A] bg-[#121212] p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold">"{d.text}"</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {d.max_price != null ? `Max $${d.max_price}` : "Any price"} · {d.matches.length} match{d.matches.length === 1 ? "" : "es"} so far
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`remove-demand-btn-${d.id}`}
                  onClick={() => remove(d.id)}
                  className="text-neutral-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 px-3"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              {d.matches.length > 0 && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {d.matches.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
